import React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FieldResolverProps {
  field: string;
  label: string;
  values: { leadId: string; leadName: string; value: string | number | null }[];
  selected: string | null;
  onSelect: (leadId: string) => void;
  highlightDifferences?: boolean;
}

export const FieldResolver: React.FC<FieldResolverProps> = ({
  field,
  label,
  values,
  selected,
  onSelect,
  highlightDifferences = true
}) => {
  // Check if all values are the same
  const allSame = values.every(v => v.value === values[0].value);
  const hasConflict = !allSame && values.some(v => v.value !== null && v.value !== '');

  // Format value for display
  const formatValue = (value: string | number | null) => {
    if (value === null || value === '') return <span className="text-gray-400 italic">Empty</span>;
    if (typeof value === 'number') {
      return new Intl.NumberFormat('en-IN').format(value);
    }
    return value;
  };

  return (
    <div className={cn(
      "p-3 rounded-lg border",
      hasConflict && highlightDifferences ? "border-orange-200 bg-orange-50" : "border-gray-200 bg-gray-50"
    )}>
      {/* Field label */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {hasConflict && (
          <span className="text-xs text-orange-600 font-medium">Conflict</span>
        )}
        {allSame && (
          <span className="text-xs text-green-600 font-medium">Same</span>
        )}
      </div>

      {/* Value options */}
      <div className="space-y-2">
        {values.map(({ leadId, leadName, value }) => {
          const isSelected = selected === leadId;
          const isEmpty = value === null || value === '';

          return (
            <button
              key={leadId}
              onClick={() => onSelect(leadId)}
              disabled={isEmpty}
              className={cn(
                "w-full flex items-center justify-between p-2 rounded-md text-left transition-all duration-150",
                isSelected
                  ? "bg-blue-100 border-2 border-blue-500"
                  : "bg-white border border-gray-200 hover:border-gray-300",
                isEmpty && "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-sm truncate",
                  isSelected ? "text-blue-900 font-medium" : "text-gray-900"
                )}>
                  {formatValue(value)}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  from {leadName}
                </p>
              </div>

              <div className="ml-2 flex-shrink-0">
                {isSelected ? (
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                ) : isEmpty ? (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <Minus className="h-3 w-3 text-gray-400" />
                  </div>
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected value preview */}
      {selected && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Will use: <span className="font-medium text-gray-700">
              {formatValue(values.find(v => v.leadId === selected)?.value ?? null)}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};
